import { AISettings, AIEvaluationResult } from '../types';

export class WebAIEvaluationService {
  private settings: AISettings;

  constructor(settings: AISettings) {
    this.settings = settings;
  }

  async evaluateAnswer(
    userAnswer: string,
    referenceAnswer: string,
    question: string,
    language: string
  ): Promise<AIEvaluationResult> {
    if (this.settings.provider === 'disabled') {
      throw new Error('AI evaluation is disabled');
    }

    const prompt = this.buildPrompt(userAnswer, referenceAnswer, question, language);

    let responseText: string;
    switch (this.settings.provider) {
      case 'openai':
        responseText = await this.callOpenAI(prompt);
        break;
      case 'anthropic':
        responseText = await this.callAnthropic(prompt);
        break;
      default:
        throw new Error(`Provider '${this.settings.provider}' is not supported in PWA mode`);
    }

    return this.parseResponse(responseText);
  }

  async testConnection(): Promise<{ success: boolean; error?: string }> {
    try {
      const testPrompt = 'Respond only with valid JSON: {"isCorrect":true,"confidence":1,"feedback":"ok","suggestions":[],"reasoning":"test"}';
      switch (this.settings.provider) {
        case 'openai':
          await this.callOpenAI(testPrompt);
          break;
        case 'anthropic':
          await this.callAnthropic(testPrompt);
          break;
        default:
          return { success: false, error: `Provider '${this.settings.provider}' is not supported in PWA mode` };
      }
      return { success: true };
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  private buildPrompt(
    userAnswer: string,
    referenceAnswer: string,
    question: string,
    language: string
  ): string {
    return `You are a code evaluation assistant. Compare the user's answer to the reference solution for a coding interview question.

Question: ${question}
Programming Language: ${language}

Reference Solution:
\`\`\`${language}
${referenceAnswer}
\`\`\`

User's Answer:
\`\`\`${language}
${userAnswer}
\`\`\`

Evaluate the user's answer and provide your response in the following JSON format:
{
  "isCorrect": boolean,
  "confidence": number (0-1),
  "feedback": "brief explanation of correctness",
  "suggestions": ["improvement suggestion 1", "improvement suggestion 2"],
  "reasoning": "detailed explanation of your evaluation"
}

Consider:
- Correctness of logic and algorithm
- Syntax and language-specific best practices
- Edge case handling
- Code efficiency and readability
- Functional equivalence to reference solution

Be constructive and educational in your feedback.`;
  }

  private async callOpenAI(prompt: string): Promise<string> {
    if (!this.settings.apiKey) throw new Error('OpenAI API key is required');

    const response = await fetch('https://api.openai.com/v1/chat/completions', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.settings.apiKey}`,
      },
      body: JSON.stringify({
        model: 'gpt-4o-mini',
        messages: [
          { role: 'system', content: 'You are a helpful code evaluation assistant.' },
          { role: 'user', content: prompt },
        ],
        temperature: this.settings.temperature ?? 0.3,
      }),
    });

    if (!response.ok) {
      const body = await response.text();
      throw new Error(`OpenAI API error ${response.status}: ${body}`);
    }

    const data = await response.json();
    return data.choices[0]?.message?.content ?? '';
  }

  private async callAnthropic(prompt: string): Promise<string> {
    if (!this.settings.apiKey) throw new Error('Anthropic API key is required');

    const response = await fetch('https://api.anthropic.com/v1/messages', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-key': this.settings.apiKey,
        'anthropic-version': '2023-06-01',
        // Required for direct browser access
        'anthropic-dangerous-direct-browser-access': 'true',
      },
      body: JSON.stringify({
        model: 'claude-haiku-4-5-20251001',
        max_tokens: 1000,
        messages: [{ role: 'user', content: prompt }],
        temperature: this.settings.temperature ?? 0.3,
      }),
    });

    if (!response.ok) {
      const body = await response.text();
      throw new Error(`Anthropic API error ${response.status}: ${body}`);
    }

    const data = await response.json();
    return data.content[0]?.text ?? '';
  }

  private parseResponse(response: string): AIEvaluationResult {
    try {
      const jsonMatch = response.match(/\{[\s\S]*\}/);
      if (!jsonMatch) throw new Error('No JSON found in response');

      const parsed = JSON.parse(jsonMatch[0]);
      if (typeof parsed.isCorrect !== 'boolean') throw new Error('Invalid isCorrect field');

      return {
        isCorrect: parsed.isCorrect,
        confidence: Math.max(0, Math.min(1, parsed.confidence ?? 0.5)),
        feedback: parsed.feedback ?? 'No feedback provided',
        suggestions: Array.isArray(parsed.suggestions) ? parsed.suggestions : [],
        reasoning: parsed.reasoning ?? 'No reasoning provided',
      };
    } catch {
      const text = response.toLowerCase();
      return {
        isCorrect: text.includes('correct') && !text.includes('incorrect'),
        confidence: 0.3,
        feedback: 'Unable to parse detailed evaluation. Please try again.',
        suggestions: ['Consider reviewing the solution approach'],
        reasoning: 'AI response parsing failed',
      };
    }
  }
}
